import { extractVipWeeklyRewardEmailReminderPreference } from "~~/shared/utils/vip-reminder.utils";

export const useUserStore = defineStore("User", {
  state: (): User => {
    return {
      authToken: "",
      id: "",
      playerId: "",
      username: "",
      displayName: "",
      tagline: "",
      usernameUpdateTime: "",
      email: "",
      walletAddress: "",
      accountAddress: "",
      keys: null,
      role: "USER",
      roles: [],
      balance: 0,
      transactions: { records: [], total: 0 },
      referralTransactions: { records: [], total: 0 },
      referredUsers: { records: [], total: 0 },
      antiphishingCode: "",
      profilePicture: "DEFAULT",
      profileBanner: "banner0",
      twoFactorAuth: false,
      communications: false,
      emailReminderPreference: "unset",
      notifications: { records: [], total: 0, unread: 0 },
      acceptTerms: false,
      requireWalletUpdate: false,
      walletLocked: false,
      identityProviders: {
        password: null,
        google: null,
        facebook: null,
        apple: null,
      },
      referredByUser: "",
      creationTime: "",
      preferredLocale: "en",
      showBalance: false,
      elementalRaidersPlayer: false,
      atelierLastRewardTime: null,
    };
  },
  getters: {
    isLoggedIn(state) {
      return !!state.authToken;
    },
    isExpired(state) {
      if (!state.authToken) return false;
      const decoded = parseJwt(state.authToken);
      const expirationDate = new Date(decoded.exp * 1000);
      const timeUntilExpiration = expirationDate.getTime() - Date.now();
      return timeUntilExpiration < 0;
    },
    isCurrentUserId(state) {
      return (id: string) => state.id === id;
    },
    isCurrentUsername(state) {
      return (user: string) => {
        if (!state.username) return false;
        return (
          state.email.toLowerCase() === user.toLowerCase() ||
          state.username.toLowerCase() === user.toLowerCase() ||
          state.accountAddress.toLowerCase() === user.toLowerCase()
        );
      };
    },
    getRoundedBalance(): number {
      return Number(this.balance.toFixed(2));
    },
    getBalanceInUSD(): number {
      if (!this.balance) return 0;
      const GFALExStore = useGFALExStore();
      if (!GFALExStore.GFALExRate) return 0;
      return this.balance * GFALExStore.GFALExRate;
    },
    hasSufficentBalance(state) {
      return (priceGFAL: number | null): boolean => {
        if (priceGFAL === null || priceGFAL === undefined) return false;
        return state.balance >= priceGFAL;
      };
    },
    transactionsByDate(state) {
      return (rowsPerPage: number) => {
        const { locale } = useI18n();
        const groupedTransactions: { [key: string]: ITransactionItem[] } = {};

        if (state.transactions.records.length < 1) return groupedTransactions;
        state.transactions.records
          .slice(0, rowsPerPage)
          .forEach((transactionItems) => {
            const date = new Date(transactionItems.time).toLocaleDateString(
              locale.value,
              {
                day: "numeric",
                month: "short",
                year: "numeric",
              },
            );
            if (!groupedTransactions[date]) {
              groupedTransactions[date] = [];
            }
            groupedTransactions[date].push(transactionItems);
          });
        return groupedTransactions;
      };
    },
    pendingTransactionsLength(state) {
      return state.transactions.records.filter(
        (transaction) => transaction.status === "PENDING",
      ).length;
    },
    uniqueTxDates() {
      return (rowsPerPage: number) =>
        Object.keys(this.transactionsByDate(rowsPerPage));
    },
    recoveryKitDate(state) {
      return state.keys?.recoveryPassword?.time;
    },
  },
  actions: {
    setWallet(user: UserPayload) {
      this.walletAddress = user.walletAddress;
      this.accountAddress = user.accountAddress;
      this.balance = user.balanceGFAL;
      this.playerId = user.playerId;
      this.showBalance = user.showBalance;
      this.transactionCount = user.transactionCount;
    },
    setKeys(keys: UserKeys) {
      this.keys = keys;
    },
    setGfalIdUser(user: GFALIDUser) {
      this.id = user.id;
      this.email = user.email;
      this.role = user.role;
      this.roles = user.roles;
      this.username = user.username;
      this.displayName = user.displayName;
      this.tagline = user.tagline;
      this.usernameUpdateTime = user.usernameUpdateTime;
      this.antiphishingCode = user.antiphishingCode;
      this.profilePicture = user.profilePicture
        ? user.profilePicture.toUpperCase()
        : "DEFAULT";
      this.profileBanner = user.profileBanner;
      this.twoFactorAuth = user.twoFactorAuth;
      this.communications = user.communications;
      this.emailReminderPreference =
        extractVipWeeklyRewardEmailReminderPreference(user) ?? "unset";
      this.acceptTerms = user.acceptTerms;
      this.identityProviders = user.identityProviders;
      this.creationTime = user.registrationTime;
      if (
        user.referrals &&
        Object.keys(user.referrals).length >= 1 &&
        "marketplace" in user.referrals
      )
        this.referredByUser = user.referrals.marketplace;
      this.preferredLocale = user.preferredLocale;
    },
    setNotifications(notifications: Notifications) {
      this.notifications.records = notifications.records;
      this.notifications.total = notifications.total;
      this.notifications.unread = notifications.unread;
    },
    setTransactions(transactionsPayload: TransactionHistoryPayload) {
      this.transactions.records = transactionsPayload.records;
      this.transactions.total = transactionsPayload.total;
    },
    setReferralTransactions(
      transactionsPayload: ReferralTransactionHistoryPayload,
    ) {
      this.referralTransactions.records =
        transactionsPayload.records as IReferralTransactionItem[];
      this.referralTransactions.total = transactionsPayload.total;
    },
    setReferredUsers(usersPayload: ReferredUserPayload) {
      this.referredUsers.records = usersPayload.records as string[];
      this.referredUsers.total = usersPayload.total;
    },
    hasKeys() {
      if (this.keys === null) return false;
      const hasPassword = this.keys.password !== null;
      const hasGoogle = this.keys.google !== null;
      const hasApple = this.keys.apple !== null;
      const hasFacebook = this.keys.facebook !== null;
      const hasRecoveryPassword = this.keys.recoveryPassword !== null;
      return (
        hasPassword ||
        hasGoogle ||
        hasApple ||
        hasFacebook ||
        hasRecoveryPassword
      );
    },
    logOut() {
      const token = useCookie("token");
      const gfalId = useCookie(`gfalid-${this.id}`);
      const wid = useCookie(`wid-${this.id}`);
      const referral = useCookie("referral");
      if (process.client) {
        localStorage.removeItem(
          `atelier-debug-balance:${this.id || this.playerId || "global"}`,
        );
        localStorage.removeItem("atelier-debug-balance:global");
      }
      token.value = null;
      gfalId.value = null;
      wid.value = null;
      referral.value = null;
      this.$reset();
    },
  },
});
